import { DateTime } from 'luxon';
import { z } from 'zod';
// ── Áreas del sistema (SRS-02 §4.2) ───────────────────────────────────────────
// Prompt BJ-11 — reestructuración del organigrama real: KAMs→Operaciones,
// Digitales→Digital, Ventas+Cobranza fusionadas en Comercial (7 áreas viejas
// → 6 nuevas), Dirección agregada. Marketing/RH/TI sin cambio. Único
// consumidor real de este array: obtenerDashboard() en tickets.service.ts
// (BJ-07) — usuarios.area/tickets.area_responsable/ticket_tipos_catalogo.area
// son string libre, ya actualizados por separado (ver catalogo_areas, la
// fuente real que alimenta los <select>).
export const AREAS = ['Operaciones', 'Comercial', 'Digital', 'Marketing', 'RH', 'TI', 'Dirección'];
// ── crearTicketSchema (SRS-02 §15) ────────────────────────────────────────────
// Multiticket: responsable_id único -> responsables (N usuario_id, mínimo 1).
//
// Prompt AL (Sub-tareas) — tipo_solicitud (string, a nivel ticket) se
// reemplaza por titulo + subtareas (una por responsable, con su propio tipo
// vía FK real a ticket_tipos_catalogo y su propio detalle). responsables
// sigue existiendo para no romper la validación de "al menos un responsable"
// de forma barata, pero el servicio deriva la lista real de responsables a
// partir de subtareas — ver crearTicket() en tickets.service.ts.
//
// Prompt AJ (Nivel de Servicio) — nivel_servicio_es_externo=false (default)
// es Interno, sin clientes. true es Externo y exige >=1 cliente_id — la
// regla se valida aquí con superRefine (autoridad real, nunca solo en
// frontend) y también en actualizarNivelServicioSchema, para el mismo caso
// en edición. Si es Interno, cliente_ids se ignora silenciosamente en el
// servicio (crearTicket) aunque venga poblado en el payload.
export const crearTicketSchema = z
    .object({
    solicitante_id: z.string().uuid(),
    titulo: z.string().min(1).max(150),
    subtareas: z
        .array(z.object({
        responsable_id: z.string().uuid(),
        tipo_solicitud_id: z.string().uuid(),
        detalle: z.string().min(10),
    }))
        .min(1, 'Se requiere al menos una sub-tarea (responsable + tipo + detalle)'),
    prioridad: z.enum(['P1', 'P2', 'P3']),
    descripcion: z.string().min(10),
    nivel_servicio_es_externo: z.boolean().default(false),
    cliente_ids: z.array(z.string().uuid()).default([]),
    adjuntos: z
        .array(z.object({
        nombre: z.string(),
        drive_url: z.string().url(),
    }))
        .optional()
        .default([]),
})
    .superRefine((data, ctx) => {
    if (data.nivel_servicio_es_externo && data.cliente_ids.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cliente_ids'],
            message: 'Se requiere al menos un cliente cuando el nivel de servicio es Externo',
        });
    }
    const responsableIds = data.subtareas.map(s => s.responsable_id);
    if (new Set(responsableIds).size !== responsableIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['subtareas'],
            message: 'Cada responsable solo puede tener una sub-tarea por ticket',
        });
    }
});
// ── actualizarNivelServicioSchema — PATCH /tickets/:id/nivel-servicio ────────
// Mismo reemplazo-completo que gestionarResponsablesSchema hace con
// agregar/quitar, pero aquí es un solo estado (es_externo + set de
// clientes), no una diferencia incremental — ver actualizarNivelServicio en
// tickets.service.ts.
export const actualizarNivelServicioSchema = z
    .object({
    nivel_servicio_es_externo: z.boolean(),
    cliente_ids: z.array(z.string().uuid()).default([]),
})
    .superRefine((data, ctx) => {
    if (data.nivel_servicio_es_externo && data.cliente_ids.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cliente_ids'],
            message: 'Se requiere al menos un cliente cuando el nivel de servicio es Externo',
        });
    }
});
// ── gestionarResponsablesSchema — PATCH /tickets/:id/responsables (root/head/analista) ──
export const gestionarResponsablesSchema = z.object({
    agregar: z.array(z.string().uuid()).default([]),
    quitar: z.array(z.string().uuid()).default([]),
}).refine(d => d.agregar.length > 0 || d.quitar.length > 0, {
    message: 'Debes indicar al menos un responsable a agregar o quitar',
});
// ── agregarSubtareaSchema — POST /tickets/:id/subtareas (Prompt AL) ──────────
// Si la persona ya tenía una fila con responsable_activo=false, el servicio
// reactiva esa misma fila conservando su detalle/estado previos (ignora
// tipo_solicitud_id/detalle de este payload en ese caso) — ver
// agregarSubtarea() en tickets.service.ts.
export const agregarSubtareaSchema = z.object({
    responsable_id: z.string().uuid(),
    tipo_solicitud_id: z.string().uuid(),
    detalle: z.string().min(10),
});
// ── actualizarSubtareaEstadoSchema — PATCH /tickets/:id/subtareas/:subtareaId ──
export const actualizarSubtareaEstadoSchema = z.object({
    estado: z.enum(['pendiente', 'completada']),
});
// ── proponerCambioPrioridadSchema — POST /tickets/:id/cambio-prioridad (Prompt AN) ──
// "Cambiar el SLA" = proponer un cambio de prioridad (el SLA se deriva de
// prioridad vía ticket_sla_config). Cualquier responsable o root puede
// proponer — la validación real de quién puede vive en el servicio.
export const proponerCambioPrioridadSchema = z.object({
    prioridad_propuesta: z.enum(['P1', 'P2', 'P3']),
    motivo: z.string().min(10),
});
// ── resolverCambioPrioridadSchema — PATCH /tickets/:id/cambio-prioridad/:propuestaId ──
// Exclusivo del solicitante_id del ticket (o root) — ver resolverCambioPrioridad().
export const resolverCambioPrioridadSchema = z.object({
    decision: z.enum(['aprobado', 'rechazado']),
});
// ── cambiarEstatusTicketSchema (SRS-02 §15) ─────────────────────────────────────────
export const cambiarEstatusTicketSchema = z.object({
    estatus: z.enum(['en_progreso', 'resuelta', 'reabierta', 'cancelada']),
});
// ── csatSchema (SRS-02 §15) ───────────────────────────────────────────────────
export const csatSchema = z.object({
    calificacion: z.number().int().min(1).max(5),
    comentario: z.string().max(1000).optional(),
});
// ── devolverTicketSchema — comentario obligatorio (prompt de reglas por-actor) ──
export const devolverTicketSchema = z.object({
    comentario: z.string().trim().min(1, 'El comentario es obligatorio').max(1000),
});
// ── dashboardQuerySchema (SRS-02 §17.4) — mes llega en ISO (yyyy-MM), se
// convierte a formato canónico dentro del servicio antes de consultar BD ────
export const dashboardQuerySchema = z.object({
    mes: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato requerido: YYYY-MM')
        .default(() => DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM')),
});
// ── Catálogo de tipos (mismo patrón que catalogos.ts — no forma parte del SRS
// explícitamente con Zod, pero sigue la convención ya establecida) ────────────
// area: string libre (no z.enum(AREAS)) — Prompt AA convierte Áreas en un
// catálogo editable (catalogo_areas); un área agregada después del enum
// estático original debe poder usarse aquí igual, sin FK física (mismo
// criterio que usuarios.area, ya un string libre).
export const crearTicketTipoSchema = z.object({
    area: z.string().min(1).max(50),
    tipo: z.string().min(1).max(100),
    descripcion: z.string().max(300).optional(),
    orden: z.coerce.number().int().optional(),
});
export const actualizarTicketTipoSchema = z.object({
    tipo: z.string().min(1).max(100).optional(),
    descripcion: z.string().max(300).nullable().optional(),
    orden: z.coerce.number().int().nullable().optional(),
});
// ── Adjuntos (referencia URL — sin integración real de Drive, Fase 3) ────────
export const agregarAdjuntoSchema = z.object({
    nombre: z.string().min(1).max(255),
    drive_url: z.string().url(),
});
// ── Formato canónico del campo `mes` (SRS-02 §17 — autoritativo; NO usar la
// versión con toLocaleDateString de §8.8, es una versión más vieja/frágil) ───
const TZ = 'America/Mexico_City';
/**
 * Genera el valor del campo `mes` a partir de una fecha.
 * Ejemplo: 2026-05-15T09:00:00 → "may-2026"
 * IMPORTANTE: siempre usa zona America/Mexico_City antes de formatear.
 *
 * NOTA: el pseudocódigo original de SRS-02 §17 no fija el locale al formatear
 * — sin `{ locale: 'es' }`, luxon usa el locale por default (inglés en este
 * entorno), produciendo "jan-2026" en vez de "ene-2026" (confirmado con una
 * prueba aislada) y contradiciendo la propia tabla de ejemplos del SRS
 * (§17.2). Se fija el locale explícitamente aquí, igual que ya hace
 * `mesAIso` al parsear en sentido inverso.
 */
export function calcularMes(fecha) {
    return DateTime.fromJSDate(fecha).setZone(TZ).setLocale('es').toFormat('MMM-yyyy').toLowerCase();
}
/**
 * Convierte un mes en formato ISO (query param del dashboard) al formato
 * canónico de almacenamiento. Ejemplo: "2026-05" → "may-2026"
 */
export function isoAMes(isoMes) {
    return DateTime.fromFormat(isoMes, 'yyyy-MM', { zone: TZ }).setLocale('es').toFormat('MMM-yyyy').toLowerCase();
}
/**
 * Convierte el formato canónico a ISO para rangos de fecha.
 * Ejemplo: "may-2026" → "2026-05"
 */
export function mesAIso(mes) {
    return DateTime.fromFormat(mes, 'MMM-yyyy', { locale: 'es' }).toFormat('yyyy-MM');
}
//# sourceMappingURL=tickets.js.map